import { useRef } from 'preact/hooks';
import { booksChanged } from '../lib/changed';
import { supabase } from '../lib/supabase';
import { useNotice } from '../shell/notice';
import {
  MARK_NAMES,
  applyMark,
  applyMove,
  applySet,
  moveMessage,
  notForMeKindOf,
  setMessage,
  undoMessage,
  type Mark,
  type MoveDetails,
  type MoveTo,
  type SetField,
} from './changes';
import { todayIn } from './model';
import type { BookData } from './types';

// The book page's changes (Part 2): each calls its database function (0025) as you, shows the
// change at once, and offers Undo in the shared notice. Undo asks the database to put back
// what the action changed; anything changed since is kept, and the notice says so.

export interface BookEdits {
  move: (to: MoveTo, details?: MoveDetails) => Promise<boolean>;
  mark: (mark: Mark, on: boolean) => Promise<boolean>;
  set: (field: SetField, value: number | string | null) => Promise<boolean>;
  /**
   * Part 3's reading actions (0026): calls the function, then reads the book again, since a
   * start, stop or finish changes several things at once. Undo the same way.
   */
  act: (fn: ReadingFn, args: Record<string, unknown>, message: string) => Promise<boolean>;
  /** Undo one of the last day's changes by its action ID (Recent changes; Kristi, Oct 27). */
  undoAction: (action: string) => Promise<void>;
}

export type ReadingFn =
  | 'read_start'
  | 'read_pause'
  | 'read_resume'
  | 'read_pause_reason'
  | 'read_pause_later'
  | 'read_progress'
  | 'read_stop'
  | 'read_finish'
  | 'read_rate'
  | 'read_copy_add'
  | 'read_copy_stop'
  | 'read_set_as'
  | 'note_add'
  | 'quote_add'
  | 'copy_add'
  | 'loan_step'
  | 'loan_not_right'
  | 'loan_quiet'
  | 'book_edit'
  | 'for_fans_of_edit'
  | 'work_audience_edit'
  | 'read_edit'
  | 'read_add_past'
  | 'read_remove'
  | 'copy_edit'
  | 'copy_remove'
  | 'loan_edit'
  | 'loan_event_edit'
  | 'loan_event_remove'
  | 'note_edit'
  | 'note_remove'
  | 'quote_edit'
  | 'quote_remove'
  | 'book_remove'
  | 'kindle_paste';

/** Functions that name their own row (a read, a copy) instead of the book. */
const NO_BOOK = new Set([
  'read_rate', 'loan_step', 'loan_not_right', 'read_edit', 'read_remove',
  'copy_edit', 'copy_remove', 'loan_edit', 'loan_event_edit', 'loan_event_remove', 'note_edit', 'note_remove', 'quote_edit', 'quote_remove',
]);

type Reload = () => Promise<BookData | null>;

async function call(fn: string, args: Record<string, unknown>): Promise<unknown> {
  if (!supabase) throw new Error('Not connected to a database.');
  const { data, error } = await supabase.rpc(fn, args);
  if (error) throw new Error(error.message);
  return data;
}

/** The changes for one book. `setBook` shows the result; `reload` reads the book again after an Undo that kept something. */
export function useBookEdits(book: BookData | null, setBook: (b: BookData) => void, reload: Reload): BookEdits {
  const notice = useNotice();
  // The latest copy, so two quick changes build on each other.
  const latest = useRef(book);
  latest.current = book;
  const show = (b: BookData | null) => {
    if (!b) return;
    latest.current = b;
    setBook(b);
  };

  const run = async (
    fn: string,
    args: Record<string, unknown>,
    after: ((b: BookData) => BookData) | 'reload',
    message: string,
    ask?: { label: string; run: () => void },
  ) => {
    const before = latest.current;
    if (!before) return false;
    let action: unknown;
    try {
      action = await call(fn, NO_BOOK.has(fn) ? args : { book: before.id, ...args });
    } catch (e) {
      notice({ message: `Not saved: ${e instanceof Error ? e.message : String(e)}`, done: false });
      return false;
    }
    booksChanged();
    if (action === null) return true; // nothing to change
    if (after === 'reload') show((await reload().catch(() => null)) ?? before);
    else show(after(before));
    notice({
      message,
      undo: typeof action === 'string' ? () => void undo(action as string, before, after === 'reload') : undefined,
      action: ask,
    });
    return true;
  };

  const undo = async (action: string, before: BookData, fresh = false) => {
    try {
      const res = (await call('undo_action', { action })) as { kept?: string[] } | null;
      const kept = res?.kept ?? [];
      booksChanged();
      if (kept.length || fresh) {
        const fresh = await reload().catch(() => null);
        show(fresh ?? before);
      } else show(before);
      notice({ message: undoMessage(before.work.title, kept) });
    } catch (e) {
      notice({ message: `Couldn’t undo: ${e instanceof Error ? e.message : String(e)}`, done: false });
    }
  };

  const now = () => new Date().toISOString();

  const setField = (field: SetField, value: number | string | null) =>
    run(
      'book_set',
      { field, value: typeof value === 'string' ? value.trim() || null : value === null ? null : String(value) },
      (b) => applySet(b, field, value, b.readTypes),
      setMessage(field, value),
    );

  return {
    move: (to, details = {}) => {
      const book = latest.current;
      if (!book) return Promise.resolve(false);
      const kind = to === 'not_for_me' ? (details.kind ?? notForMeKindOf(book)) : undefined;
      const sent: Record<string, unknown> = {};
      if (kind) sent.kind = kind;
      if (details.chips?.length) sent.chips = details.chips;
      if (details.reason?.trim()) sent.reason = details.reason.trim();
      if (details.place) sent.place = details.place;
      // Kristi, Oct 28: a move to Reference asks "Should it be Study?" rather than changing it.
      const study = studyQuestion(book, to);
      return run(
        'book_move',
        { to_shelf: to, details: sent },
        (b) => applyMove(b, to, { ...details, kind }, now(), todayIn()),
        moveMessage(book.work.title, to, kind) + (study ? '. Should it be Study?' : ''),
        study ? { label: 'Make it Study', run: () => void setField('read_type', study) } : undefined,
      );
    },
    mark: (mark, on) =>
      run('book_mark', { mark, turn_on: on }, (b) => applyMark(b, mark, on, now()), `${MARK_NAMES[mark]} ${on ? 'on' : 'off'} for ${latest.current?.work.title ?? 'the book'}`),
    set: setField,
    act: (fn, args, message) => run(fn, args, 'reload', message),
    undoAction: (action) => (latest.current ? undo(action, latest.current, true) : Promise.resolve()),
  };
}

/**
 * A move to Reference asks whether the book should be Study (Kristi, Oct 28), when it isn't
 * already and you have a read type called Study: the name to set, or null for no question.
 */
export function studyQuestion(book: Pick<BookData, 'readType' | 'readTypes'>, to: MoveTo): string | null {
  if (to !== 'reference') return null;
  const study = book.readTypes.find((t) => t.name.trim().toLowerCase() === 'study');
  if (!study || book.readType?.name.trim().toLowerCase() === 'study') return null;
  return study.name;
}
